import { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Lock } from 'lucide-react';
import GooglePlacesAutocomplete from '../GooglePlacesAutocomplete';
import { GTA_CITIES, TORONTO_NEIGHBORHOODS } from '../../lib/listingConstants';

/**
 * Location step.
 *
 * We use Google Places Autocomplete for the address, then store:
 *   - address_line_1 (private, full street)
 *   - city (canonicalised server-side; Toronto neighborhoods become Toronto)
 *   - postal_code
 *   - address_lat, address_lng (exact)
 *
 * The server fuzzes exact → approximate_location (±500m) on save.
 */
const StepLocation = ({ listing, patchDraft, onNext, saving }) => {
  const [addr1, setAddr1] = useState(listing.address_line_1 || '');
  const [addr2, setAddr2] = useState(listing.address_line_2 || '');
  const [city, setCity] = useState(listing.city || '');
  const [postal, setPostal] = useState(listing.postal_code || '');
  const [neighborhood, setNeighborhood] = useState(listing.neighborhood || '');
  const [lat, setLat] = useState(listing.address_lat || null);
  const [lng, setLng] = useState(listing.address_lng || null);
  const [error, setError] = useState('');

  const canContinue = addr1.trim() && city.trim() && postal.trim() && lat && lng;

  const onPlace = (p) => {
    if (p.formatted_address) setAddr1(p.formatted_address);
    if (p.city) setCity(p.city);
    if (p.neighborhood && !neighborhood) setNeighborhood(p.neighborhood);
    if (p.lat) setLat(p.lat);
    if (p.lng) setLng(p.lng);
  };

  const save = async () => {
    setError('');
    try {
      await patchDraft({
        address_line_1: addr1,
        address_line_2: addr2 || null,
        city,
        postal_code: postal,
        neighborhood: neighborhood || null,
        address_lat: Number(lat),
        address_lng: Number(lng),
      });
      onNext();
    } catch (e) {
      setError(
        typeof e.response?.data?.detail === 'string'
          ? e.response.data.detail
          : 'Something went wrong saving the address.'
      );
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-extrabold text-slate-900">Where is it?</h1>
        <p className="mt-1 text-sm text-slate-600">
          We cover the Greater Toronto Area: {GTA_CITIES.slice(0, 6).join(', ')}… and Toronto
          neighborhoods like {TORONTO_NEIGHBORHOODS.slice(0, 3).join(', ')}.
        </p>
      </div>

      <Card className="bg-amber-50 border-amber-200/60">
        <CardContent className="p-4 flex gap-2">
          <Lock className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-900 leading-relaxed">
            Your exact address stays private. Students see a general area (within ~500 m)
            until a lease is signed.
          </p>
        </CardContent>
      </Card>

      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-2 block">Search address</Label>
        <GooglePlacesAutocomplete
          type="city"
          placeholder="Start typing a street or address"
          value={addr1}
          onChange={setAddr1}
          onSelect={onPlace}
          countries={['ca']}
        />
      </div>

      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-2 block">Street address</Label>
        <Input
          value={addr1}
          onChange={(e) => setAddr1(e.target.value)}
          placeholder="123 Main Street"
          className="h-12"
          data-testid="address-line-1"
        />
      </div>
      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-2 block">Unit / suite (optional)</Label>
        <Input
          value={addr2}
          onChange={(e) => setAddr2(e.target.value)}
          placeholder="Apt 301"
          className="h-12"
          data-testid="address-line-2"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-sm font-semibold text-slate-900 mb-2 block">City</Label>
          <Input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Toronto"
            className="h-12"
            data-testid="city-input"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-900 mb-2 block">Postal code</Label>
          <Input
            value={postal}
            onChange={(e) => setPostal(e.target.value.toUpperCase())}
            placeholder="M5H 2N2"
            maxLength={7}
            className="h-12"
            data-testid="postal-input"
          />
        </div>
      </div>

      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-2 block">Neighborhood (optional)</Label>
        <Input
          value={neighborhood}
          onChange={(e) => setNeighborhood(e.target.value)}
          placeholder="Little Italy, Scarborough, Port Credit..."
          className="h-12"
          data-testid="neighborhood-input"
        />
      </div>

      {error && (
        <div className="px-4 py-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700" data-testid="location-error">
          {error}
        </div>
      )}

      <Button
        className="w-full btn-primary h-12 text-base"
        disabled={!canContinue || saving}
        onClick={save}
        data-testid="step-location-continue"
      >
        Continue
      </Button>
    </div>
  );
};

export default StepLocation;
